import { apiRequest } from './apiClient';
import { t } from '../utils/translations';

interface UserData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface UpdateUserRequest {
  firstName: string;
  lastName: string;
}

interface UpdateUserResponse {
  message: string;
}

export const getUserData = async (): Promise<UserData> => {
  try {
    const response = await apiRequest.get('api/user', { requiresHouseholdId: false });

    if (!response.ok) {
      const errorData = await response.json();
      const translatedMessage = t(`apiErrors.${errorData.message}`) !== `apiErrors.${errorData.message}`
        ? t(`apiErrors.${errorData.message}`)
        : errorData.message;

      if (response.status === 401) {
        throw new Error(t('user.unauthorized'));
      } else if (response.status === 403) {
        throw new Error(t('user.forbidden'));
      } else if (response.status >= 500) {
        throw new Error(t('user.serverError'));
      } else {
        throw new Error(translatedMessage || t('user.loadFailed'));
      }
    }

    return response.json();
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(t('user.connectionError'));
    }
    throw error;
  }
};

export const updateUser = async (data: UpdateUserRequest): Promise<UpdateUserResponse> => {
  try {
    const response = await apiRequest.patch('api/user', data);

    if (!response.ok) {
      const errorData = await response.json();
      const translatedMessage = t(`apiErrors.${errorData.message}`) !== `apiErrors.${errorData.message}`
        ? t(`apiErrors.${errorData.message}`)
        : errorData.message;

      if (response.status === 422) {
        throw new Error(translatedMessage || t('user.validationError'));
      } else if (response.status === 401) {
        throw new Error(t('user.unauthorized'));
      } else if (response.status === 403) {
        throw new Error(t('user.forbidden'));
      } else if (response.status >= 500) {
        throw new Error(t('user.serverError'));
      } else {
        throw new Error(translatedMessage || t('user.updateFailed'));
      }
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    return { message: 'Success' };
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(t('user.connectionError'));
    }
    throw error;
  }
};

interface RequestPasswordResetRequest {
  email: string;
  recaptchaToken: string;
}

export const requestPasswordReset = async (data: RequestPasswordResetRequest): Promise<void> => {
  try {
    const response = await apiRequest.post('api/user/forgot-password', data, { requiresHouseholdId: false });

    if (!response.ok) {
      const errorData = await response.json();
      const translatedMessage = t(`apiErrors.${errorData.message}`) !== `apiErrors.${errorData.message}`
        ? t(`apiErrors.${errorData.message}`)
        : errorData.message;

      if (response.status === 422) {
        throw new Error(translatedMessage || t('user.validationError'));
      } else if (response.status >= 500) {
        throw new Error(t('user.serverError'));
      } else {
        throw new Error(translatedMessage || t('user.passwordResetFailed'));
      }
    }
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(t('user.connectionError'));
    }
    throw error;
  }
};

interface ResendActivationEmailRequest {
  email: string;
  recaptchaToken: string;
}

export const resendActivationEmail = async (data: ResendActivationEmailRequest): Promise<void> => {
  try {
    const response = await apiRequest.post('api/user/resend-activation-email', data, { requiresHouseholdId: false });

    if (!response.ok) {
      const errorData = await response.json();
      const translatedMessage = t(`apiErrors.${errorData.message}`) !== `apiErrors.${errorData.message}`
        ? t(`apiErrors.${errorData.message}`)
        : errorData.message;

      if (response.status === 422) {
        throw new Error(translatedMessage || t('user.validationError'));
      } else if (response.status >= 500) {
        throw new Error(t('user.serverError'));
      } else {
        throw new Error(translatedMessage || t('user.resendActivationFailed'));
      }
    }
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error(t('user.connectionError'));
    }
    throw error;
  }
};
